import { useEffect, useMemo } from 'react';
import FormSelect from 'components/FormFields/FormSelect';
import { getCurrencySettings } from 'components/Modal/Project/utils/currency';
import { PieChart, Pie, Cell, Legend, ResponsiveContainer, Label } from 'recharts';
import { centsToDollars } from 'utils/general';
import { Spinner } from 'components/Loading';
import clsx from 'clsx';

function revenueDisplay(amount, currency) {
  return centsToDollars(amount, currency, null, true); // Remove the decimal and cents for display
}

const CenterLabel = ({ viewBox, totalAmount, currency }) => {
  const { cx, cy } = viewBox;
  return (
    <>
      <text
        x={cx}
        y={cy - 20}
        fill="#333"
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: '14px', fontWeight: '400' }}
      >
        Total Invoiced:
      </text>
      <text
        x={cx}
        y={cy + 15}
        fill="#333"
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: '36px', fontWeight: '500', fontFamily: '"Playfair Display", serif' }}
      >
        {revenueDisplay(totalAmount, currency)}
      </text>
    </>
  );
};

const FinancialChart = ({ financialInfo, showCTA, onNoData, currency, setCurrency }) => {
  const { symbol } = getCurrencySettings(currency);

  // Derive the chart data from the shared financialInfo prop so the widget stays
  // in sync with the dashboard's source of truth (and re-renders on currency change).
  const chartData = useMemo(() => {
    if (!financialInfo) return null;
    const { total_amount, paid_in_full, unpaid, overdue, partial } = financialInfo || {};

    return {
      data: [
        {
          name: 'Paid',
          value: paid_in_full,
          displayValue: revenueDisplay(paid_in_full, currency),
          color: '#AD8E55',
        },
        {
          name: 'Unpaid',
          value: unpaid,
          displayValue: revenueDisplay(unpaid, currency),
          color: '#EEE9E6',
        },
        {
          name: 'Partial',
          value: partial,
          displayValue: revenueDisplay(partial, currency),
          color: '#B8B8B8',
        },
        {
          name: 'Overdue',
          value: overdue,
          displayValue: revenueDisplay(overdue, currency),
          color: '#A05748',
        },
      ],
      total: total_amount,
    };
  }, [financialInfo, currency]);

  // Surface the empty state to the parent whenever the summary resolves.
  useEffect(() => {
    if (!financialInfo) return;
    onNoData(!financialInfo.invoices.length);
  }, [financialInfo, onNoData]);

  if (!chartData) return (
    <div className="financial-loader">
      <Spinner />
    </div>
  );

  return (
    <div className="financial-chart">
      <FormSelect
        name="currency"
        onChange={(e) => setCurrency(e.target.value)}
        value={currency}
      >
        {financialInfo.currencies.map((option, index) => (
          <option
            value={option}
            key={`currency-${index}`}
          >
            {option}
          </option>
        ))}
      </FormSelect>
      { !showCTA && (
        <>
          <div className={clsx('canvas-container', !showCTA && 'populated')}>
            <ResponsiveContainer
              width="100%"
              height={365}
            >
              <PieChart>
                <Pie
                  data={chartData?.data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={130}
                  outerRadius={140}
                >
                  {chartData?.data.map((d, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={d.color}
                    />
                  ))}
                  <Label
                    content={(
                      <CenterLabel
                        totalAmount={chartData?.total || 0}
                        symbol={symbol}
                        currency={currency}
                      />
                    )}
                    position="center"
                  />
                </Pie>
                <Legend
                  iconType="circle"
                  layout="horizontal"
                  verticalAlign="bottom"
                  width="100%"
                  align="center"
                  formatter={(value, entry) => (
                    <>
                      <span className='legend-label'>{value}</span>
                      <span className='legend-display'>{entry.payload.displayValue}</span>
                    </>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default FinancialChart;
